<template>
  <div class="settings-admin">
    <div class="level">
      <div class="level-left">
        <div class="level-item">
          <h1 class="title is-4">Settings</h1>
        </div>
      </div>
      <div class="level-right">
        <div class="level-item">
          <b-input
              v-model="search"
              placeholder="Search by name or value..."
              type="search"
              icon="search"
              @input="onSearch"
          />
        </div>
        <div class="level-item">
          <b-button type="is-primary" icon-left="plus" @click="openCreate">
            New setting
          </b-button>
        </div>
      </div>
    </div>

    <b-table
        :data="settings"
        :loading="loading"
        :paginated="true"
        :per-page="perPage"
        :total="total"
        :current-page.sync="page"
        backend-pagination
        @page-change="onPageChange"
        hoverable
        striped
    >
      <b-table-column field="name" label="Name" v-slot="props">
        <code>{{ props.row.name }}</code>
      </b-table-column>

      <b-table-column field="value" label="Value" v-slot="props">
        <span class="value-cell">{{ props.row.value }}</span>
      </b-table-column>

      <b-table-column field="encrypted" label="Type" width="120" centered v-slot="props">
        <b-tag :type="props.row.encrypted ? 'is-warning' : 'is-light'">
          {{ props.row.encrypted ? 'encrypted' : 'Plain' }}
        </b-tag>
      </b-table-column>

      <b-table-column label="Actions" width="120" centered v-slot="props">
        <div class="buttons is-centered">
          <b-button
              size="is-small"
              icon-left="pencil"
              @click="openEdit(props.row)"
          />
          <b-button
              size="is-small"
              type="is-danger"
              icon-left="trash"
              @click="confirmDelete(props.row)"
          />
        </div>
      </b-table-column>

      <template #empty>
        <div class="has-text-centered has-text-grey p-4">
          No settings found.
        </div>
      </template>
    </b-table>

    <!-- Create / Edit modal -->
    <b-modal
        :active.sync="isModalActive"
        has-modal-card
        trap-focus
        :destroy-on-hide="true"
        aria-role="dialog"
        aria-modal
    >
      <div class="modal-card" style="width: 480px">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ isEditing ? 'Edit setting' : 'New setting' }}
          </p>
          <button type="button" class="delete" @click="closeModal" />
        </header>

        <section class="modal-card-body">
          <b-field
              label="Name"
              :type="errors.name ? 'is-danger' : ''"
              :message="errors.name"
          >
            <b-input
                v-model.trim="form.name"
                placeholder="e.g. smtp_password"
                :disabled="isEditing"
                expanded
            />
          </b-field>

          <b-field
              label="Value"
              :type="errors.value ? 'is-danger' : ''"
              :message="errors.value"
          >
            <b-input
                v-model="form.value"
                type="textarea"
                placeholder="Value to store..."
                expanded
            />
          </b-field>

          <b-field>
            <b-switch v-model="form.encrypted">
              Store encrypted
            </b-switch>
          </b-field>
          <p class="help" v-if="form.encrypted">
            The value will be encrypted at rest and shown decrypted here.
          </p>
        </section>

        <footer class="modal-card-foot is-justify-content-flex-end">
          <b-button label="Cancel" @click="closeModal" />
          <b-button
              :label="isEditing ? 'Update' : 'Create'"
              type="is-primary"
              :loading="saving"
              @click="save"
          />
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'

const ENDPOINT = 'admin/settings'

function emptyForm () {
  return { id: null, name: '', value: '', encrypted: false }
}

export default {
  name: 'SettingsAdmin',

  data () {
    return {
      settings: [],
      loading: false,
      saving: false,
      search: '',
      page: 1,
      perPage: 15,
      total: 0,
      isModalActive: false,
      isEditing: false,
      form: emptyForm(),
      errors: {}
    }
  },

  created () {
    this.onSearch = debounce(() => {
      this.page = 1
      this.fetch()
    }, 350)
    this.fetch()
  },

  methods: {
    async fetch () {
      this.loading = true
      try {
        const { data } = await this.$http.get(ENDPOINT, {
          params: {
            search: this.search || undefined,
            page: this.page,
            per_page: this.perPage
          }
        })
        // Assumes a Laravel paginator response
        this.settings = data.data
        this.total = data.meta.total
      } catch (e) {
        this.notifyError('Could not load settings.')
      } finally {
        this.loading = false
      }
    },

    onPageChange (page) {
      this.page = page
      this.fetch()
    },

    openCreate () {
      this.isEditing = false
      this.form = emptyForm()
      this.errors = {}
      this.isModalActive = true
    },

    openEdit (row) {
      this.isEditing = true
      this.form = { id: row.id, name: row.name, value: row.value, encrypted: row.encrypted }
      this.errors = {}
      this.isModalActive = true
    },

    closeModal () {
      this.isModalActive = false
    },

    validate () {
      this.errors = {}
      if (!this.form.name) this.errors.name = 'Name is required.'
      if (this.form.value === null || this.form.value === undefined || this.form.value === '') {
        this.errors.value = 'Value is required.'
      }
      return Object.keys(this.errors).length === 0
    },

    async save () {
      if (!this.validate()) return
      this.saving = true
      try {
        const payload = {
          name: this.form.name,
          value: this.form.value,
          encrypted: this.form.encrypted
        }

        if (this.isEditing) {
          await this.$http.put(`${ENDPOINT}/${this.form.id}`, payload)
        } else {
          await this.$http.post(ENDPOINT, payload)
        }

        this.$buefy.toast.open({
          message: this.isEditing ? 'Setting updated.' : 'Setting created.',
          type: 'is-success'
        })
        this.closeModal()
        this.fetch()
      } catch (e) {
        // Map Laravel validation errors (422) if present
        const bag = e?.response?.data?.errors
        if (bag) {
          this.errors = Object.keys(bag).reduce((acc, k) => {
            acc[k] = bag[k][0]
            return acc
          }, {})
        } else {
          this.notifyError('Could not save the setting.')
        }
      } finally {
        this.saving = false
      }
    },

    confirmDelete (row) {
      this.$buefy.dialog.confirm({
        title: 'Delete setting',
        message: `Delete <b>${row.name}</b>? This cannot be undone.`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.remove(row)
      })
    },

    async remove (row) {
      try {
        await this.$http.delete(`${ENDPOINT}/${row.id}`)
        this.$buefy.toast.open({ message: 'Setting deleted.', type: 'is-success' })
        this.fetch()
      } catch (e) {
        this.notifyError('Could not delete the setting.')
      }
    },

    notifyError (message) {
      this.$buefy.toast.open({ message, type: 'is-danger' })
    }
  }
}
</script>

<style scoped>
.value-cell {
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
</style>